/**
 * Centralized Error Handling Utility
 * Provides consistent error handling and user-friendly messages
 */

export class AppError extends Error {
  constructor(message, code = 'UNKNOWN_ERROR', statusCode = 500, originalError = null) {
    super(message);
    this.name = 'AppError';
    this.code = code;
    this.statusCode = statusCode;
    this.originalError = originalError;
    this.timestamp = new Date()?.toISOString();
  }
}

export const errorHandler = {
  // Parse Supabase errors
  parseSupabaseError(error) {
    if (!error) return null;

    // Network errors
    if (error?.message?.includes('Failed to fetch') || error?.message?.includes('NetworkError')) {
      return new AppError(
        'Service temporarily unavailable. Please try again in a moment.',
        'NETWORK_ERROR',
        0,
        error
      );
    }

    // Edge Function / service errors
    if (error?.message?.includes('Failed to send a request to the Edge Function') || error?.message?.includes('Edge Function')) {
      return new AppError(
        'Email service is temporarily unavailable. Please try again in a moment.',
        'EDGE_FUNCTION_ERROR',
        503,
        error
      );
    }

    // Authentication errors
    if (error?.message?.includes('Invalid login credentials')) {
      return new AppError(
        'Invalid email or password. Please try again.',
        'AUTH_INVALID_CREDENTIALS',
        401,
        error
      );
    }

    if (error?.message?.includes('Email not confirmed')) {
      return new AppError(
        'Please verify your email address before logging in.',
        'AUTH_EMAIL_NOT_CONFIRMED',
        401,
        error
      );
    }

    if (error?.message?.includes('User already registered')) {
      return new AppError(
        'An account with this email already exists. Please login instead.',
        'AUTH_USER_EXISTS',
        409,
        error
      );
    }

    // Database errors
    if (error?.code === '23505') {
      return new AppError(
        'This email address is already registered. Please use a different email or sign in.',
        'DB_DUPLICATE_ENTRY',
        409,
        error
      );
    }

    if (error?.code === '23503') {
      return new AppError(
        'Cannot complete this action due to related data. Please contact support.',
        'DB_FOREIGN_KEY_VIOLATION',
        400,
        error
      );
    }

    if (error?.message?.includes('violates row-level security')) {
      return new AppError(
        'You do not have permission to perform this action.',
        'AUTH_PERMISSION_DENIED',
        403,
        error
      );
    }

    // Timeout errors
    if (error?.message?.includes('timeout') || error?.message?.includes('Request timeout')) {
      return new AppError(
        'Request timed out. Please try again.',
        'REQUEST_TIMEOUT',
        408,
        error
      );
    }

    // Generic error
    return new AppError(
      error.message || 'An unexpected error occurred. Please try again.',
      'UNKNOWN_ERROR',
      500,
      error
    );
  },

  // Handle async operations with error catching
  async handleAsync(asyncFn, errorContext = '') {
    try {
      return await asyncFn();
    } catch (error) {
      const appError = this.parseSupabaseError(error);
      console.error(`[Error Handler] ${errorContext}:`, {
        message: appError?.message,
        code: appError?.code,
        timestamp: appError?.timestamp,
        original: error
      });
      throw appError;
    }
  },

  // Retry logic for failed operations
  async withRetry(asyncFn, options = {}) {
    const {
      maxRetries = 3,
      retryDelay = 1000,
      backoff = true,
      onRetry = null
    } = options;

    let lastError;
    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        return await asyncFn();
      } catch (error) {
        lastError = error;
        
        // Don't retry on auth errors or client errors (4xx)
        const appError = this.parseSupabaseError(error);
        if (appError?.statusCode >= 400 && appError?.statusCode < 500) {
          throw appError;
        }

        if (attempt < maxRetries) {
          const delay = backoff ? retryDelay * attempt : retryDelay;
          console.warn(`[Retry] Attempt ${attempt}/${maxRetries} failed. Retrying in ${delay}ms...`);
          
          if (onRetry) {
            onRetry(attempt, delay);
          }
          
          await new Promise(resolve => setTimeout(resolve, delay));
        }
      }
    }

    throw this.parseSupabaseError(lastError);
  },

  // Log error for monitoring
  logError(error, context = {}) {
    const errorLog = {
      message: error?.message,
      code: error?.code || 'UNKNOWN',
      timestamp: new Date()?.toISOString(),
      context,
      stack: error?.stack
    };

    // In production, send to error tracking service (e.g., Sentry)
    console.error('[Error Log]', errorLog);
    
    // Store in localStorage for debugging (limit to last 50 errors)
    try {
      const errors = JSON.parse(localStorage.getItem('app_errors') || '[]');
      errors?.unshift(errorLog);
      localStorage.setItem('app_errors', JSON.stringify(errors?.slice(0, 50)));
    } catch (e) {
      // Ignore localStorage errors
    }
  }
};

export default errorHandler;